import {ReactElement} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {Country} from "../../region/types/Country";
import {useAppSelector} from "../../store/hooks";
import {HomeMatchBasicPreferencesFlow} from "../../user/components/home_match_basic_preferences_flow/HomeMatchBasicPreferencesFlow";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {leadModalZindex} from "../constants/lead/lead_modal_zindex";
import {useApplicationModalArray} from "../hooks/use_application_form_modal_array";
import {useLeadRequiredParams} from "../hooks/use_lead_required_params";
import {usePromotionLeadFlow} from "../hooks/use_promotion_lead_flow";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationData} from "../types/IApplicationData";
import {RecommendationSource} from "../types/lead/RecommendationSource";
import {IApplicationMultiLeadSummaryStepState} from "../types/multi_lead/IApplicationMultiLeadSummaryStepState";
import {ApplicationSource} from "../utils/form/application_form_source";
import {getShouldRequestHomeMatchBasicPreferences} from "../utils/lead/get_should_request_home_match_basic_preferences";
import {ApplicationGoodbyeContentModal} from "./goodbye_content_modal/ApplicationGoodbyeContentModal";
import {LeadFormModalAskForFullOffer} from "./lead_form_modal/LeadFormModalAskForFullOffer";
import {LeadFormModalPromotion} from "./lead_form_modal/LeadFormModalPromotion";
import {LeadFormModalStandard} from "./lead_form_modal/LeadFormModalStandard";
import {LeadFormModalWithNegotiation} from "./lead_form_modal/LeadFormModalWithNegotiation";
import {LeadFormModalWithTopic} from "./lead_form_modal/LeadFormModalWithTopic";
import {ApplicationMultileadModalStep} from "./multi_lead/ApplicationMultileadModalStep";
import {ApplicationNotificationConsentConfirmationModal} from "./notification_consent_confirmation_modal/ApplicationNotificationConsentConfirmationModal";
import {PromotionLeadPhoneVerification} from "./promotion_flow/PromotionLeadPhoneVerification";
import {PromotionLeadSuccess} from "./promotion_flow/PromotionLeadSuccess";
import {ApplicationSuccessInfoModal} from "./success_info_modal/ApplicationSuccessInfoModal";

type ApplicationStepRenderMode = "standard_modal" | "self_managed_modal" | "hidden";
type ApplicationStepLayoutVariant = "form" | "full";

interface IApplicationStepRenderConfig {
    content: ReactElement | null;
    renderMode: ApplicationStepRenderMode;
    layoutVariant?: ApplicationStepLayoutVariant;
    shouldCloseOnOverlayClick?: boolean;
}

export const ApplicationModalArray = () => {
    const {propertyId, offerId} = useLeadRequiredParams();

    const {modalState, sendModalEvent, isOpened, source, initialLeadFormValues, leadData} = useApplicationModalArray();

    const initialStep = useAppSelector((state) => state.mainLead.initialStep);
    const homeMatchBasicEventCategory = useAppSelector((state) => state.mainLead.trackingMeta.openButtonSource) || "";
    const roomCount = leadData?.property?.detail?.rooms as number;
    const propertyPrice = (leadData?.property?.detail?.price ?? null) as number | null;
    const isAbroad = leadData?.offer?.detail?.region.country !== Country.POLAND;
    const isMultileadStepEnabled = !!propertyId && !isAbroad;
    const recommendationSource = source === ApplicationSource.ApplicationOfferScheduleMeeting ? RecommendationSource.MODEL : RecommendationSource.DISTANCE;
    const originalApplication = leadData?.application || null;
    const isAuthenticated = useIsAuthenticated();
    const promotionLeadDraft = useAppSelector((state) => state.mainLead.promotionLeadDraft);
    const leadFormInitialValues = promotionLeadDraft || initialLeadFormValues;

    const shouldRequestHomeMatchBasicPreferences = (application: IApplicationData | null | undefined) =>
        getShouldRequestHomeMatchBasicPreferences({
            source,
            isAuthenticated,
            application
        });

    const onModalClose = () => {
        sendModalEvent({type: "FORM_CLOSED"});
    };

    const onSingleLeadSuccess = async (
        sentApplication: IApplicationData | null,
        _offer?: IOfferDetailApplication,
        _property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => {
        const needsMarketingConsent = !!sentApplication && !sentApplication.marketing_email && !sentApplication.marketing_sms;
        const recommendationsWithPriceFilter = recommendedFilters ? "max_price" in recommendedFilters : false;

        sendModalEvent(
            {type: "FORM_SUBMITTED"},
            {originalApplicationResponse: sentApplication, recommendationsWithPriceFilter},
            {needsMarketingConsent, needsHomeMatchBasicPreferences: shouldRequestHomeMatchBasicPreferences(sentApplication)}
        );
    };

    const promotionLeadFlow = usePromotionLeadFlow({
        handleModalClose: onModalClose,
        modalState,
        sendModalEvent
    });

    const getStepRenderConfig = (): IApplicationStepRenderConfig => {
        switch (modalState.step) {
            case ApplicationModalStep.APPLICATION_FORM_STANDARD: {
                return {
                    content: leadFormInitialValues ? (
                        <LeadFormModalStandard
                            handleModalClose={onModalClose}
                            onSuccessfulSubmit={onSingleLeadSuccess}
                            initialLeadFormValues={leadFormInitialValues}
                            onSubmitDraft={promotionLeadFlow.handleDraftSubmit}
                        />
                    ) : null,
                    renderMode: "standard_modal",
                    layoutVariant: "form",
                    shouldCloseOnOverlayClick: false
                };
            }
            case ApplicationModalStep.APPLICATION_FORM_FULL_OFFER: {
                return {
                    content: (
                        <LeadFormModalAskForFullOffer
                            handleModalClose={onModalClose}
                            onSuccessfulSubmit={onSingleLeadSuccess}
                            initialLeadFormValues={leadFormInitialValues}
                            onSubmitDraft={promotionLeadFlow.handleDraftSubmit}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION: {
                return {
                    content: (
                        <LeadFormModalWithNegotiation
                            handleModalClose={onModalClose}
                            onSuccessfulSubmit={onSingleLeadSuccess}
                            initialLeadFormValues={leadFormInitialValues}
                            onSubmitDraft={promotionLeadFlow.handleDraftSubmit}
                            price={propertyPrice}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.APPLICATION_FORM_WITH_TOPIC: {
                return {
                    content: (
                        <LeadFormModalWithTopic
                            handleModalClose={onModalClose}
                            onSuccessfulSubmit={onSingleLeadSuccess}
                            initialLeadFormValues={leadFormInitialValues}
                            onSubmitDraft={promotionLeadFlow.handleDraftSubmit}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.PROMOTION_FORM: {
                return {
                    content: <LeadFormModalPromotion handleModalClose={onModalClose} onSubmitDraft={promotionLeadFlow.handleDraftSubmit} />,
                    renderMode: "standard_modal",
                    layoutVariant: "form",
                    shouldCloseOnOverlayClick: false
                };
            }
            case ApplicationModalStep.PROMOTION_PHONE_VERIFICATION: {
                return {
                    content: (
                        <PromotionLeadPhoneVerification
                            isOpen
                            onClose={onModalClose}
                            onSuccess={promotionLeadFlow.handlePhoneVerificationSuccess}
                            onChangeNumber={promotionLeadFlow.handlePhoneVerificationChangeNumber}
                        />
                    ),
                    renderMode: "self_managed_modal"
                };
            }
            case ApplicationModalStep.PROMOTION_AUTH_FALLBACK: {
                return {
                    content: null,
                    renderMode: "hidden"
                };
            }
            case ApplicationModalStep.PROMOTION_SUBMITTING: {
                return {
                    content: (
                        <div className={waitingWrapperStyle}>
                            <Loader size="lg" fullWidth />
                        </div>
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.PROMOTION_SUCCESS: {
                return {
                    content: <PromotionLeadSuccess isOpen onClose={onModalClose} />,
                    renderMode: "self_managed_modal"
                };
            }
            case ApplicationModalStep.HOME_MATCH_BASIC_PREFERENCES: {
                return {
                    content: (
                        <HomeMatchBasicPreferencesFlow
                            isOpen
                            source="afterLead"
                            initialStep="auto"
                            defaultHomeMatchBasicConsent
                            propertyId={propertyId}
                            offerId={offerId}
                            originalApplicationResponse={modalState.data.originalApplicationResponse || originalApplication}
                            trackingContext={{
                                eventName: "modal_post_lead",
                                eventCategory: homeMatchBasicEventCategory,
                                sourceModal: "modal_post_lead"
                            }}
                            onComplete={() => sendModalEvent({type: "HOME_MATCH_BASIC_COMPLETED"}, undefined, {needsHomeMatchBasicPreferences: false})}
                        />
                    ),
                    renderMode: "self_managed_modal"
                };
            }
            case ApplicationModalStep.NOTIFICATION_CONSENT: {
                return {
                    content: (
                        <ApplicationNotificationConsentConfirmationModal
                            result={modalState.data.originalApplicationResponse}
                            onConsentComplete={() => sendModalEvent({type: "CONSENT_COMPLETED"})}
                            onModalClose={onModalClose}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.SUCCESS_INFO: {
                return {
                    content: (
                        <ApplicationSuccessInfoModal
                            isModal
                            originalApplicationResponse={modalState.data.originalApplicationResponse}
                            onClick={() => sendModalEvent({type: "SUCCESS_INFO_NEXT"})}
                            shouldRenderRecommendations
                            onModalClose={onModalClose}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.GOODBYE_INFO: {
                return {
                    content: <ApplicationGoodbyeContentModal closeModal={onModalClose} />,
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.MULTILEAD: {
                if (!isMultileadStepEnabled && initialStep !== ApplicationModalStep.MULTILEAD) {
                    notifyBugsnag("renderContent - LeadModalStep.MULTILEAD", "Multilead is disabled");
                    onModalClose();
                    return {
                        content: null,
                        renderMode: "hidden"
                    };
                }

                return {
                    content: (
                        <ApplicationMultileadModalStep
                            originalApplicationResponse={modalState.data.originalApplicationResponse || originalApplication}
                            recommendationsWithPriceFilter={modalState.data.recommendationsWithPriceFilter}
                            roomCount={roomCount}
                            isAbroad={isAbroad}
                            recommendationSource={recommendationSource}
                            onNoRecommendations={(originalApplicationResponse: IApplicationData | null) => {
                                const needsMarketingConsent =
                                    !!originalApplicationResponse && !originalApplicationResponse.marketing_email && !originalApplicationResponse.marketing_sms;
                                sendModalEvent(
                                    {type: "MULTILEAD_NO_RECOMMENDATIONS"},
                                    {originalApplicationResponse},
                                    {needsMarketingConsent, needsHomeMatchBasicPreferences: shouldRequestHomeMatchBasicPreferences(originalApplicationResponse)}
                                );
                            }}
                            onClose={() => sendModalEvent({type: "MULTILEAD_CLOSED"})}
                            onMultiLeadComplete={(_payload: IApplicationMultiLeadSummaryStepState) => {
                                const app = modalState.data.originalApplicationResponse;
                                const needsMarketingConsent = !!app && !app.marketing_email && !app.marketing_sms;
                                sendModalEvent({type: "MULTILEAD_COMPLETED"}, undefined, {
                                    needsMarketingConsent,
                                    needsHomeMatchBasicPreferences: shouldRequestHomeMatchBasicPreferences(app)
                                });
                            }}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.WAITING:
            default:
                return {
                    content: (
                        <div className={waitingWrapperStyle}>
                            <Loader size="lg" fullWidth />
                        </div>
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
        }
    };

    const stepRenderConfig = getStepRenderConfig();

    if (stepRenderConfig.renderMode === "hidden") {
        return null;
    }

    if (stepRenderConfig.renderMode === "self_managed_modal") {
        return stepRenderConfig.content;
    }

    return (
        <Modal
            isOpen={isOpened}
            onModalClose={() => onModalClose()}
            shouldCloseOnOverlayClick={stepRenderConfig.shouldCloseOnOverlayClick ?? true}
            zIndex={leadModalZindex}
            showCloseButton={false}
            fullScreen="xs"
        >
            {stepRenderConfig.layoutVariant === "form" ? (
                <div className={applicationWrapStyle}>
                    <div className={applicationHolderStyle}>{stepRenderConfig.content}</div>
                </div>
            ) : (
                <div className={applicationWrapStyle}>
                    <div className={applicationHolderFullStyle}>{stepRenderConfig.content}</div>
                </div>
            )}
        </Modal>
    );
};

const applicationWrapStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    overflow: "hidden",
    height: "100%"
});

const applicationHolderStyle = css({
    width: "100%",
    minWidth: "28rem",
    overflowY: "auto",
    position: "relative"
});

const applicationHolderFullStyle = css({
    width: "100%",
    height: "100%",
    minWidth: "28rem",
    overflowY: "auto",
    position: "relative"
});

const waitingWrapperStyle = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "24rem",
    md: {
        width: "560px",
        height: "auto",
        minHeight: "28rem"
    }
});
import {ReactElement} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";

import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {IOfferDetailApplication} from "../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../property/types/IPropertyDetailApplication";
import {Country} from "../../region/types/Country";
import {useAppSelector} from "../../store/hooks";
import {HomeMatchBasicPreferencesFlow} from "../../user/components/home_match_basic_preferences_flow/HomeMatchBasicPreferencesFlow";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {leadModalZindex} from "../constants/lead/lead_modal_zindex";
import {useApplicationModalArray} from "../hooks/use_application_form_modal_array";
import {useLeadRequiredParams} from "../hooks/use_lead_required_params";
import {usePromotionLeadFlow} from "../hooks/use_promotion_lead_flow";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationData} from "../types/IApplicationData";
import {RecommendationSource} from "../types/lead/RecommendationSource";
import {IApplicationMultiLeadSummaryStepState} from "../types/multi_lead/IApplicationMultiLeadSummaryStepState";
import {ApplicationSource} from "../utils/form/application_form_source";
import {getShouldRequestHomeMatchBasicPreferences} from "../utils/lead/get_should_request_home_match_basic_preferences";
import {ApplicationGoodbyeContentModal} from "./goodbye_content_modal/ApplicationGoodbyeContentModal";
import {LeadFormModalAskForFullOffer} from "./lead_form_modal/LeadFormModalAskForFullOffer";
import {LeadFormModalPromotion} from "./lead_form_modal/LeadFormModalPromotion";
import {LeadFormModalStandard} from "./lead_form_modal/LeadFormModalStandard";
import {LeadFormModalWithNegotiation} from "./lead_form_modal/LeadFormModalWithNegotiation";
import {LeadFormModalWithTopic} from "./lead_form_modal/LeadFormModalWithTopic";
import {ApplicationMultileadModalStep} from "./multi_lead/ApplicationMultileadModalStep";
import {ApplicationNotificationConsentConfirmationModal} from "./notification_consent_confirmation_modal/ApplicationNotificationConsentConfirmationModal";
import {PromotionLeadPhoneVerification} from "./promotion_flow/PromotionLeadPhoneVerification";
import {PromotionLeadSuccess} from "./promotion_flow/PromotionLeadSuccess";
import {ApplicationSuccessInfoModal} from "./success_info_modal/ApplicationSuccessInfoModal";

type ApplicationStepRenderMode = "standard_modal" | "self_managed_modal" | "hidden";
type ApplicationStepLayoutVariant = "form" | "full";

interface IApplicationStepRenderConfig {
    content: ReactElement | null;
    renderMode: ApplicationStepRenderMode;
    layoutVariant?: ApplicationStepLayoutVariant;
    shouldCloseOnOverlayClick?: boolean;
}

export const ApplicationModalArray = () => {
    const {propertyId, offerId} = useLeadRequiredParams();

    const {modalState, sendModalEvent, isOpened, source, initialLeadFormValues, leadData} = useApplicationModalArray();

    const initialStep = useAppSelector((state) => state.mainLead.initialStep);
    const homeMatchBasicEventCategory = useAppSelector((state) => state.mainLead.trackingMeta.openButtonSource) || "";
    const roomCount = leadData?.property?.detail?.rooms as number;
    const propertyPrice = (leadData?.property?.detail?.price ?? null) as number | null;
    const isAbroad = leadData?.offer?.detail?.region.country !== Country.POLAND;
    const isMultileadStepEnabled = !!propertyId && !isAbroad;
    const recommendationSource = source === ApplicationSource.ApplicationOfferScheduleMeeting ? RecommendationSource.MODEL : RecommendationSource.DISTANCE;
    const originalApplication = leadData?.application || null;
    const isAuthenticated = useIsAuthenticated();
    const promotionLeadDraft = useAppSelector((state) => state.mainLead.promotionLeadDraft);
    const leadFormInitialValues = promotionLeadDraft || initialLeadFormValues;

    const shouldRequestHomeMatchBasicPreferences = (application: IApplicationData | null | undefined) =>
        getShouldRequestHomeMatchBasicPreferences({
            source,
            isAuthenticated,
            application
        });

    const onModalClose = () => {
        sendModalEvent({type: "FORM_CLOSED"});
    };

    const onSingleLeadSuccess = async (
        sentApplication: IApplicationData | null,
        _offer?: IOfferDetailApplication,
        _property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => {
        const needsMarketingConsent = !!sentApplication && !sentApplication.marketing_email && !sentApplication.marketing_sms;
        const recommendationsWithPriceFilter = recommendedFilters ? "max_price" in recommendedFilters : false;

        sendModalEvent(
            {type: "FORM_SUBMITTED"},
            {originalApplicationResponse: sentApplication, recommendationsWithPriceFilter},
            {needsMarketingConsent, needsHomeMatchBasicPreferences: shouldRequestHomeMatchBasicPreferences(sentApplication)}
        );
    };

    const promotionLeadFlow = usePromotionLeadFlow({
        handleModalClose: onModalClose,
        modalState,
        sendModalEvent
    });

    const getStepRenderConfig = (): IApplicationStepRenderConfig => {
        switch (modalState.step) {
            case ApplicationModalStep.APPLICATION_FORM_STANDARD: {
                return {
                    content: leadFormInitialValues ? (
                        <LeadFormModalStandard
                            handleModalClose={onModalClose}
                            onSuccessfulSubmit={onSingleLeadSuccess}
                            initialLeadFormValues={leadFormInitialValues}
                            onSubmitDraft={promotionLeadFlow.handleDraftSubmit}
                        />
                    ) : null,
                    renderMode: "standard_modal",
                    layoutVariant: "form",
                    shouldCloseOnOverlayClick: false
                };
            }
            case ApplicationModalStep.APPLICATION_FORM_FULL_OFFER: {
                return {
                    content: (
                        <LeadFormModalAskForFullOffer
                            handleModalClose={onModalClose}
                            onSuccessfulSubmit={onSingleLeadSuccess}
                            initialLeadFormValues={leadFormInitialValues}
                            onSubmitDraft={promotionLeadFlow.handleDraftSubmit}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION: {
                return {
                    content: (
                        <LeadFormModalWithNegotiation
                            handleModalClose={onModalClose}
                            onSuccessfulSubmit={onSingleLeadSuccess}
                            initialLeadFormValues={leadFormInitialValues}
                            onSubmitDraft={promotionLeadFlow.handleDraftSubmit}
                            price={propertyPrice}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.APPLICATION_FORM_WITH_TOPIC: {
                return {
                    content: (
                        <LeadFormModalWithTopic
                            handleModalClose={onModalClose}
                            onSuccessfulSubmit={onSingleLeadSuccess}
                            initialLeadFormValues={leadFormInitialValues}
                            onSubmitDraft={promotionLeadFlow.handleDraftSubmit}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.PROMOTION_FORM: {
                return {
                    content: <LeadFormModalPromotion handleModalClose={onModalClose} onSubmitDraft={promotionLeadFlow.handleDraftSubmit} />,
                    renderMode: "standard_modal",
                    layoutVariant: "form",
                    shouldCloseOnOverlayClick: false
                };
            }
            case ApplicationModalStep.PROMOTION_PHONE_VERIFICATION: {
                return {
                    content: (
                        <PromotionLeadPhoneVerification
                            isOpen
                            onClose={onModalClose}
                            onSuccess={promotionLeadFlow.handlePhoneVerificationSuccess}
                            onChangeNumber={promotionLeadFlow.handlePhoneVerificationChangeNumber}
                        />
                    ),
                    renderMode: "self_managed_modal"
                };
            }
            case ApplicationModalStep.PROMOTION_AUTH_FALLBACK: {
                return {
                    content: null,
                    renderMode: "hidden"
                };
            }
            case ApplicationModalStep.PROMOTION_SUBMITTING: {
                return {
                    content: (
                        <div className={waitingWrapperStyle}>
                            <Loader size="lg" fullWidth />
                        </div>
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.PROMOTION_SUCCESS: {
                return {
                    content: <PromotionLeadSuccess isOpen onClose={onModalClose} />,
                    renderMode: "self_managed_modal"
                };
            }
            case ApplicationModalStep.HOME_MATCH_BASIC_PREFERENCES: {
                return {
                    content: (
                        <HomeMatchBasicPreferencesFlow
                            isOpen
                            source="afterLead"
                            initialStep="auto"
                            defaultHomeMatchBasicConsent
                            propertyId={propertyId}
                            offerId={offerId}
                            originalApplicationResponse={modalState.data.originalApplicationResponse || originalApplication}
                            trackingContext={{
                                eventName: "modal_post_lead",
                                eventCategory: homeMatchBasicEventCategory,
                                sourceModal: "modal_post_lead"
                            }}
                            onComplete={() => sendModalEvent({type: "HOME_MATCH_BASIC_COMPLETED"}, undefined, {needsHomeMatchBasicPreferences: false})}
                        />
                    ),
                    renderMode: "self_managed_modal"
                };
            }
            case ApplicationModalStep.NOTIFICATION_CONSENT: {
                return {
                    content: (
                        <ApplicationNotificationConsentConfirmationModal
                            result={modalState.data.originalApplicationResponse}
                            onConsentComplete={() => sendModalEvent({type: "CONSENT_COMPLETED"})}
                            onModalClose={onModalClose}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.SUCCESS_INFO: {
                return {
                    content: (
                        <ApplicationSuccessInfoModal
                            isModal
                            originalApplicationResponse={modalState.data.originalApplicationResponse}
                            onClick={() => sendModalEvent({type: "SUCCESS_INFO_NEXT"})}
                            shouldRenderRecommendations
                            onModalClose={onModalClose}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.GOODBYE_INFO: {
                return {
                    content: <ApplicationGoodbyeContentModal closeModal={onModalClose} />,
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.MULTILEAD: {
                if (!isMultileadStepEnabled && initialStep !== ApplicationModalStep.MULTILEAD) {
                    notifyBugsnag("renderContent - LeadModalStep.MULTILEAD", "Multilead is disabled");
                    onModalClose();
                    return {
                        content: null,
                        renderMode: "hidden"
                    };
                }

                return {
                    content: (
                        <ApplicationMultileadModalStep
                            originalApplicationResponse={modalState.data.originalApplicationResponse || originalApplication}
                            recommendationsWithPriceFilter={modalState.data.recommendationsWithPriceFilter}
                            roomCount={roomCount}
                            isAbroad={isAbroad}
                            recommendationSource={recommendationSource}
                            onNoRecommendations={(originalApplicationResponse: IApplicationData | null) => {
                                const needsMarketingConsent =
                                    !!originalApplicationResponse && !originalApplicationResponse.marketing_email && !originalApplicationResponse.marketing_sms;
                                sendModalEvent(
                                    {type: "MULTILEAD_NO_RECOMMENDATIONS"},
                                    {originalApplicationResponse},
                                    {needsMarketingConsent, needsHomeMatchBasicPreferences: shouldRequestHomeMatchBasicPreferences(originalApplicationResponse)}
                                );
                            }}
                            onClose={() => sendModalEvent({type: "MULTILEAD_CLOSED"})}
                            onMultiLeadComplete={(_payload: IApplicationMultiLeadSummaryStepState) => {
                                const app = modalState.data.originalApplicationResponse;
                                const needsMarketingConsent = !!app && !app.marketing_email && !app.marketing_sms;
                                sendModalEvent({type: "MULTILEAD_COMPLETED"}, undefined, {
                                    needsMarketingConsent,
                                    needsHomeMatchBasicPreferences: shouldRequestHomeMatchBasicPreferences(app)
                                });
                            }}
                        />
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
            }
            case ApplicationModalStep.WAITING:
            default:
                return {
                    content: (
                        <div className={waitingWrapperStyle}>
                            <Loader size="lg" fullWidth />
                        </div>
                    ),
                    renderMode: "standard_modal",
                    layoutVariant: "full"
                };
        }
    };

    const stepRenderConfig = getStepRenderConfig();

    if (stepRenderConfig.renderMode === "hidden") {
        return null;
    }

    if (stepRenderConfig.renderMode === "self_managed_modal") {
        return stepRenderConfig.content;
    }

    return (
        <Modal
            isOpen={isOpened}
            onModalClose={() => onModalClose()}
            shouldCloseOnOverlayClick={stepRenderConfig.shouldCloseOnOverlayClick ?? true}
            zIndex={leadModalZindex}
            showCloseButton={false}
            fullScreen="xs"
        >
            {stepRenderConfig.layoutVariant === "form" ? (
                <div className={applicationWrapStyle}>
                    <div className={applicationHolderStyle}>{stepRenderConfig.content}</div>
                </div>
            ) : (
                <div className={applicationWrapStyle}>
                    <div className={applicationHolderFullStyle}>{stepRenderConfig.content}</div>
                </div>
            )}
        </Modal>
    );
};

const applicationWrapStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    overflow: "hidden",
    height: "100%"
});

const applicationHolderStyle = css({
    width: "100%",
    minWidth: "28rem",
    overflowY: "auto",
    position: "relative"
});

const applicationHolderFullStyle = css({
    width: "100%",
    height: "100%",
    minWidth: "28rem",
    overflowY: "auto",
    position: "relative"
});

const waitingWrapperStyle = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "24rem",
    md: {
        width: "560px",
        height: "auto",
        minHeight: "28rem"
    }
});
